import { inject, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  ParamMap,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {
  BoelsCatalogVersion,
  BoelsCategoryHierarchy,
  BOProduct,
  PageType,
} from '@boels/models';
import {
  Observable,
  of,
  combineLatest,
  filter,
  map,
  switchMap,
  tap,
  catchError,
} from 'rxjs';
import { LinksService, UrlSafePipe } from '@boels/helpers';
import { HttpStatusCode } from '@angular/common/http';
import { CatalogsFacade } from '../../catalog/facade/catalogs.facade';
import { CategoriesFacade } from '../../categories/facade/categories.facade';
import { CmsFacade } from '../../cms/facade/cms.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { ProductFacade } from '../../product';
import { RedirectService } from '../../redirect/redirect.service';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { BoelsAlternatesFacade } from '../../alternates/facade/boels-alternates.facade';

export const ProductRouteGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> => {
  return route.paramMap.keys.length > 0
    ? isCmsPage(route, state)
    : handleRouteNotFound();
};

const handleRouteNotFound = (): Observable<boolean> => {
  const cmsFacade = inject(CmsFacade);
  cmsFacade.loadPage('not-found', PageType.CONTENT_PAGE);
  return cmsFacade.isCmsRoute$;
};

const isCmsPage = (
  activatedRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> => {
  const path = state.url.split('/').slice(1).join('/');
  const pageCode = getPageCode(path);
  const pageType = getPageType(state.url);
  const queryParams = state.root.queryParamMap;

  const services = injectServices();
  const { cmsFacade } = services;
  cmsFacade.loadPage(pageCode, pageType);

  return combineLatest([cmsFacade.activeLoaded$, cmsFacade.isCmsRoute$]).pipe(
    map((latest) => latest[1]),
    switchMap((isCmsRoute) => {
      if (
        pageType === PageType.PRODUCT_PAGE &&
        activatedRoute.params['productCode']
      ) {
        return renamedProductRedirect(
          state.url,
          queryParams,
          activatedRoute.params['productCode'],
          services,
        );
      }
      if (pageType === PageType.CATEGORY_PAGE) {
        return renamedCategoryRedirect(
          state.url,
          queryParams,
          activatedRoute,
          services,
        );
      }
      return of(isCmsRoute);
    }),
    catchError(() => {
      handleRouteNotFound();
      return of(false);
    }),
  );
};

const getPageCode = (path: string): string => {
  const search_path = '/search';
  const search_full_path = '/search/find';
  return path.includes(search_full_path)
    ? search_path
    : path.substring(path.lastIndexOf('/') + 1).split('?')[0];
};

const getPageType = (url: string): PageType => {
  if (url.includes('/c/') || url.includes('/category/'))
    return PageType.CATEGORY_PAGE;
  if (url.includes('/search')) return PageType.CONTENT_PAGE;
  return PageType.PRODUCT_PAGE;
};

const renamedProductRedirect = (
  route: string,
  queryParams: ParamMap,
  productCode: string,
  services: ReturnType<typeof injectServices>,
): Observable<boolean> => {
  const { linkService, router, redirectService, productFacade, urlSafePipe } =
    services;

  return productFacade.get(productCode).pipe(
    filter(Boolean),
    switchMap((product: BOProduct) => {
      const urlParts = [
        urlSafePipe.transform(product?.boelsBreadcrumbs?.[0]?.categoryName),
        urlSafePipe.transform(product.name),
        'p',
        product.code,
      ];
      const result = router.createUrlTree(urlParts);

      if (!route.includes(result.toString())) {
        const label = linkService
          .urlBuilder(result.toString())
          .replace('/', '');
        const paramsString = queryParams.keys.some(Boolean)
          ? '?' +
            queryParams.keys
              .map((key) => `${key}=${queryParams.get(key)}`)
              .join('&')
          : '';
        return redirectService
          .redirect(label + paramsString, HttpStatusCode.MovedPermanently)
          .pipe(map(() => false));
      }
      return of(true);
    }),
  );
};

const renamedCategoryRedirect = (
  url: string,
  queryParams: ParamMap,
  route: ActivatedRouteSnapshot,
  services: ReturnType<typeof injectServices>,
): Observable<boolean> => {
  const {
    catalogsFacade,
    router,
    redirectService,
    categoriesFacade,
    urlSafePipe,
    siteContextFacade,
    culturesFacade,
    alternatesFacade,
  } = services;

  return combineLatest([
    culturesFacade.getActiveCulture(),
    siteContextFacade.getActiveBaseSiteEntity(),
  ]).pipe(
    switchMap(([culture, activeBaseSite]) => {
      if (!activeBaseSite?.uid) return of(false);

      return combineLatest([
        catalogsFacade.getCategoryForId(
          activeBaseSite.uid,
          culture,
          route.params['categoryId'],
        ),
        catalogsFacade.getCatalogs().pipe(filter(Boolean)),
        catalogsFacade.loading$,
        catalogsFacade.success$,
        catalogsFacade.error$,
      ]).pipe(
        filter(
          ([, , loading, success, error]) => !loading && (error || success),
        ),
        tap(([category]) => {
          if (category?.id) {
            categoriesFacade.setActiveCategory(category.id);
            alternatesFacade.loadAlternates(category.id);
          }
        }),
        switchMap(([category, catalogs]) => {
          if (category?.id) {
            return categoriesFacade.getActiveCategory().pipe(
              filter((activeCategory) => activeCategory === category.id),
              switchMap(() =>
                handleExistingCategory(
                  category,
                  url,
                  queryParams,
                  router,
                  redirectService,
                  urlSafePipe,
                ),
              ),
            );
          } else {
            return handleNonExistingCategory(
              url,
              catalogs,
              queryParams,
              router,
              redirectService,
              urlSafePipe,
            );
          }
        }),
      );
    }),
  );
};

const handleExistingCategory = (
  category: BoelsCategoryHierarchy,
  url: string,
  queryParams: ParamMap,
  router: Router,
  redirectService: RedirectService,
  urlSafePipe: UrlSafePipe,
) => {
  const urlParts = [
    ...(category.boelsBreadcrumbs?.map((breadcrumb) =>
      urlSafePipe.transform(breadcrumb.categoryName),
    ) || []),
    urlSafePipe.transform(category.name),
    'c',
    category.id,
  ];
  const result = router.createUrlTree(urlParts);

  if (!url.includes(result.toString())) {
    const paramsString = queryParams.keys.some(Boolean)
      ? '?' +
        queryParams.keys
          .map((key) => `${key}=${queryParams.get(key)}`)
          .join('&')
      : '';
    return redirectService
      .redirect(
        result.toString() + paramsString,
        HttpStatusCode.MovedPermanently,
      )
      .pipe(map(() => false));
  }
  return of(true);
};

const handleNonExistingCategory = (
  url: string,
  catalogs: BoelsCatalogVersion,
  queryParams: ParamMap,
  router: Router,
  redirectService: RedirectService,
  urlSafePipe: UrlSafePipe,
) => {
  const urlSegments = url.split('/');
  const activeCategoryId = urlSegments[urlSegments.length - 1];
  const activeCategory = catalogs.categories[0].subcategories?.find(
    (category) => category.id === activeCategoryId,
  );

  if (activeCategory) {
    const activeCategoryName = urlSafePipe.transform(activeCategory.name);
    const activeCategoryParentName = urlSafePipe.transform(
      activeCategory.boelsBreadcrumbs?.[0].categoryName,
    );

    if (activeCategoryName && !router.url.includes(activeCategoryName)) {
      const url = router.createUrlTree(
        [activeCategoryParentName, activeCategoryName, 'c', activeCategoryId],
        { queryParamsHandling: 'merge', queryParams },
      );
      router.navigateByUrl(url);
    }
    return of(false);
  } else {
    redirectService.noCaching();
    redirectService.setStatusCode(HttpStatusCode.NotFound);
    return of(true);
  }
};

const injectServices = () => ({
  cmsFacade: inject(CmsFacade),
  urlSafePipe: inject(UrlSafePipe),
  linkService: inject(LinksService),
  redirectService: inject(RedirectService),
  router: inject(Router),
  catalogsFacade: inject(CatalogsFacade),
  categoriesFacade: inject(CategoriesFacade),
  productFacade: inject(ProductFacade),
  culturesFacade: inject(CultureFacade),
  siteContextFacade: inject(SiteContextFacade),
  alternatesFacade: inject(BoelsAlternatesFacade),
  platformBrowser: inject(PLATFORM_ID),
});
